import { CalendarRangePicker } from '@shared/components/CalendarRangePicker';
import { Box, FormControl, TextV2 } from '@withjoy/joykit';
import { useField } from 'formik';
import React, { useMemo } from 'react';
import * as Yup from 'yup';
import { SchemaDefaultValues } from './helpers';
import * as DateFNS from 'date-fns';
import { Calendar } from '@withjoy/joykit/icons';
import { useTranslation } from '@shared/core';
import { useIsMobileOrTablet } from '@shared/utils/media/useMediaScreens';
import { DateRange } from '@shared/components/CalendarRangePicker/CalendarRangePicker';

const schema = Yup.object({
  startDate: Yup.date(),
  endDate: Yup.date()
}).test('dates', 'Invalid dates', function (value) {
  if (!value) {
    return false;
  }
  return value.startDate && value.endDate;
});
// TODO: type this based on Yup.InferType<typeof schema> in a way that works with eslint
type ISchema = {
  startDate: Date | null;
  endDate: Date | null;
};

const initialValue: SchemaDefaultValues<typeof schema> = {
  startDate: undefined,
  endDate: undefined
};

const getDateRangeDisplay = (startDate: Date | null, endDate: Date | null) => {
  const fmtDate = (date: Date | null) => (date ? DateFNS.format(date, 'MMM d') : '');
  // Thur, Jan 1 - Sun, Jan 4
  return `${fmtDate(startDate)} - ${fmtDate(endDate)}`;
};

const Fields = ({ fieldsId, defaultEndDate, defaultStartDate }: { fieldsId: string; defaultStartDate?: Date; defaultEndDate?: Date }) => {
  // using DateRange as an uncontrolled component
  const [field, meta, helper] = useField<ISchema>(fieldsId);
  const isMobileOrTablet = useIsMobileOrTablet();
  const { t } = useTranslation('stays');
  const datesOccupantsTrans = t('datesOccupants');
  const handleChange = ([start, end]: [Date | undefined, Date | undefined]) => {
    helper.setValue({
      startDate: start || null,
      endDate: end || null
    });
  };
  const errorProps = useMemo(() => {
    return {
      isInvalid: meta.touched && !!meta.error,
      error: (meta.touched && meta.error) || undefined
    };
  }, [meta.touched, meta.error]);

  return (
    <FormControl {...errorProps} zIndex={1100} marginTop={{ _: 8, md: 0 }}>
      {isMobileOrTablet ? (
        <>
          <TextV2 typographyVariant={'hed3'} fontSize={19}>
            {datesOccupantsTrans.selectDates()}
          </TextV2>
          <DateRange onChange={handleChange} initial={[field.value.startDate || defaultStartDate, field.value.endDate || defaultEndDate]} />
        </>
      ) : (
        <CalendarRangePicker initial={[field.value.startDate || defaultStartDate, field.value.endDate || defaultEndDate]} onChange={handleChange} closeOnSelectDate>
          <FakeInput>{getDateRangeDisplay(field.value.startDate, field.value.endDate)}</FakeInput>
        </CalendarRangePicker>
      )}
    </FormControl>
  );
};

const FakeInput = React.forwardRef<HTMLDivElement, { children: React.ReactNode }>(({ children, ...restProps }, ref) => {
  return (
    <Box
      ref={ref}
      // spread required for FakeInput to work with PopoverV2
      {...restProps}
      display={'flex'}
      alignItems={'center'}
      cursor={'pointer'}
      borderRadius={'0.25rem'}
      paddingX={'1rem'}
      whiteSpace={'nowrap'}
      gap={3}
      typographyVariant={'label2'}
    >
      <Calendar />
      {children}
    </Box>
  );
});

FakeInput.displayName = 'FakeInput';

export { Fields, schema, initialValue };
